import {
    ApiCart,
    ApiClient,
    ApiFullLandingPageLink,
    ApiPlaylist,
    ApiReview,
    ApiReviewMediaItem,
    BrandChatMessage,
    BrandChatReply,
    BrandChatStorefrontContext,
    CART_SOURCE_FRONT_END,
    CART_SOURCE_KEY,
    GetExternalLandingPageProductsResponse,
    isProductFamiliesQueryEnabled,
    PRODUCT_FAMILIES_PARAM,
} from '@storefronts/common-types'
import { Cart, CartItem } from './storefront/types'
import {
    getCartTokenCookie,
    localAndCookieKeys,
    setCartTokenCookie,
} from './storefront/storage'
import { fetchWithRetries } from './storefront/utils/helpers'
import { mapCartItemsToApiLines } from './storefront/utils/cartTools'
import { readTurnEvents } from './utils/turnEvents'

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASEURL
const nextPrefix = process.env.NEXT_PUBLIC_API_PREFIX || ''

const buildCartAttributes = (
    shopifyY: string | null | undefined,
): Record<string, string> | undefined => {
    if (!shopifyY) {
        return undefined
    }
    return { _bcai_aid: shopifyY }
}

type ApiResponse<Result> =
    | {
          isError: false
          result: Result
      }
    | {
          isError: true
          error: string
          warn?: boolean
      }

type UpsertCartResponse = ApiResponse<ApiCart>

type InitializeCartResponse = ApiResponse<ApiCart>

export type AddCartLineItemsResponse = ApiResponse<ApiCart>

export type RemoveCartLineItemsResponse = ApiResponse<ApiCart>

export type UpdateCartLineItemsResponse = ApiResponse<ApiCart>

type GetCartResponse = ApiResponse<ApiCart>

type GetReviewsResponse = ApiResponse<{
    reviews: ApiReview[]
}>

type GetReviewMediaResponse = ApiResponse<{
    media: ApiReviewMediaItem[]
    hasMore: boolean
}>

// Creates a blank Shopify cart using a tokenless Storefront-api.
const createBlankCart = async (
    client: ApiClient,
): Promise<{ id: string; checkoutUrl: string } | undefined> => {
    const response = await fetchWithRetries(
        `https://${client.shopifyShop}/api/2026-01/graphql.json`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                query: `mutation ($input: CartInput!) {
                    cartCreate(input: $input) {
                        cart {
                            id
                            checkoutUrl
                        }
                    }
                }`,
                variables: {
                    input: {
                        lines: [],
                        attributes: [
                            {
                                key: CART_SOURCE_KEY,
                                value: CART_SOURCE_FRONT_END,
                            },
                        ],
                    },
                },
            }),
        },
    )
    let statusMessage = `${response.status} - ${response.statusText}`
    if (response.ok) {
        const resp = await response.json()
        if (resp?.data?.cartCreate?.cart) {
            const cart = resp.data.cartCreate.cart
            console.log(
                `Using cart created from tokenless API on ${client.displayName ?? client.clientHash}`,
            )
            return {
                id: cart.id,
                checkoutUrl: cart.checkoutUrl,
            }
        }
        statusMessage =
            resp?.errors
                ?.map(({ message }: { message: string }) => message)
                ?.join(',') ?? 'Invalid cart response'
    }
    console.warn(
        `Failed to create cart using tokenless storefront API on ${client.displayName ?? client.clientHash}: ${statusMessage}`,
    )
}

// Indicates if the user has a valid cart for the landingpage/client
const hasValidCart = async (
    cartUuid: string,
    landingPageHash: string,
): Promise<boolean> => {
    try {
        const cart = await getStorefrontCart(cartUuid, landingPageHash)
        return !cart.isError && !!cart?.result?.id
    } catch {}
    return false
}

// Indicates if a cartToken is pointing to a valid cart.
const isCartTokenValid = async (
    cartGid: string,
    clientHash: string,
): Promise<boolean> => {
    const res = await fetchWithRetries(
        `${apiBaseUrl}/external/client/${clientHash}/cart/id/${cartGid}?partial=true`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        },
    )
    // Assume not valid?
    if (!res.ok) {
        return false
    }
    const resp = await res.json()
    // Failure to find a cart usually returns an error response
    return !resp?.isError && !!resp.result?.id
}

export const getCart = async (
    cartUuid: string,
    clientHash: string,
    landingPageHash: string,
): Promise<GetCartResponse> => {
    const res = await fetchWithRetries(
        `${apiBaseUrl}/external/client/${clientHash}/cart/${cartUuid}?landingPageHash=${landingPageHash}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        },
    )
    if (!res.ok) {
        return {
            isError: true,
            error: res.statusText,
        }
    }
    const resp = await res.json()
    return resp as GetCartResponse
}

export const getStorefrontCart = async (
    cartUuid: string,
    landingPageHash: string,
): Promise<GetCartResponse> => {
    const res = await fetchWithRetries(
        `${apiBaseUrl}/external/landing/${landingPageHash}/cart/${cartUuid}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        },
    )
    if (!res.ok) {
        return {
            isError: true,
            error: res.statusText,
        }
    }
    const resp = await res.json()
    return resp as GetCartResponse
}

export const initializeCart = async (
    client: ApiClient,
    cartUuid: string,
    landingPageHash: string,
    discountCodes?: string[],
    eventParams?: {
        shop?: string
        eventId?: string | null
        pEventId?: string | null
    },
    shareCart: boolean = false,
): Promise<InitializeCartResponse> => {
    const [localShopifyY, cookieShopifyY] = localAndCookieKeys('_shopify_y')
    const [localBCAIId] = localAndCookieKeys('_bcai_aid')
    const shopifyY = cookieShopifyY || localShopifyY || localBCAIId

    let cartToken = undefined

    // For now gating this on a query param for testing purposes.
    if (shareCart) {
        // Fetch the Shopify storefront cartToken from the `cart` cookie.
        // TODO: probably should limit this when we're on a shop's actual domain.
        cartToken = getCartTokenCookie()
        if (
            cartToken &&
            !(await isCartTokenValid(cartToken, client.clientHash))
        ) {
            // cartToken is not pointing to a valid cart.
            cartToken = undefined
        }
    }

    // Shopify Confirmation Email Hack
    if (!cartToken) {
        // Seeing if we can get around the Shopify confirmation email bug by using a cart that is created
        // from the tokenless client-side storefront-api.
        // Let's validate if this could get around the confirmation email issue by leveraging our cart adoption
        // mechanism that we use to share a pre-existing Shopify cart form the partner's online shop.
        // cartToken isn't set so we're either not sharing a cart, or there isn't an existing cart cookie.
        try {
            // Cart adoption only happens if there isn't a valid cart.
            if (!(await hasValidCart(cartUuid, landingPageHash))) {
                const cart = await createBlankCart(client)
                if (cart?.id) {
                    // Successfully created a cart, let's pass it as the cartToken to adopt.
                    cartToken = cart.id.replace('gid://shopify/Cart/', '')
                }
            }
        } catch (error) {
            console.warn(
                'Tokenless cart creation failure in initializeCart',
                error,
            )
        }
    }

    // TODO: Eventually switch everything to the same endpoint?
    const endpoint = eventParams
        ? `${nextPrefix}/api/client/${client.clientHash}/cart/${cartUuid}`
        : `${apiBaseUrl}/external/client/${client.clientHash}/cart/${cartUuid}`

    const res = await fetchWithRetries(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            landingPageHash,
            discountCodes,
            cartToken,
            attributes: buildCartAttributes(shopifyY),
            ...(eventParams && { event: eventParams }),
        }),
    })

    if (!res.ok) {
        return {
            isError: true,
            error: res.statusText,
        }
    }

    const resp = (await res.json()) as InitializeCartResponse

    // For now gating this on a query param for testing purposes.
    if (shareCart) {
        // If there's no existing cartToken, let's use ours.
        if (!cartToken && !resp.isError && resp.result.id) {
            // TODO: Probably should limit this to when we're on the shop's domain.
            // TODO: move cart token conversion to util? (cartTools?)
            setCartTokenCookie(
                resp.result.id.replace('gid://shopify/Cart/', ''),
            )
        }
    }

    return resp
}

function mapLines(items?: CartItem[], isUpdateOperation: boolean = false) {
    return mapCartItemsToApiLines(items, isUpdateOperation)
}

function mapDeleteLines(items?: CartItem[]) {
    if (!items) {
        return []
    }
    return items.map((item) => ({
        id: item.shopifyLineId,
    }))
}

export const addCartLineItems = async (
    client: ApiClient,
    cartUuid: string,
    landingPageHash: string,
    addedItems: CartItem[],
): Promise<AddCartLineItemsResponse> => {
    const res = await fetchWithRetries(
        `${apiBaseUrl}/external/client/${client.clientHash}/cart/${cartUuid}/add`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                landingPageHash,
                addedItems: mapLines(addedItems),
            }),
        },
    )
    if (!res.ok) {
        return {
            isError: true,
            error: res.statusText,
        }
    }
    const resp = await res.json()
    return resp as AddCartLineItemsResponse
}

export const removeCartLineItems = async (
    client: ApiClient,
    cartUuid: string,
    landingPageHash: string,
    deletedItems: CartItem[],
): Promise<RemoveCartLineItemsResponse> => {
    const res = await fetchWithRetries(
        `${apiBaseUrl}/external/client/${client.clientHash}/cart/${cartUuid}/remove`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                landingPageHash,
                deletedItems: mapDeleteLines(deletedItems),
            }),
        },
    )
    if (!res.ok) {
        return {
            isError: true,
            error: res.statusText,
        }
    }
    const resp = await res.json()
    return resp as RemoveCartLineItemsResponse
}

export const updateCartLineItems = async (
    client: ApiClient,
    cartUuid: string,
    landingPageHash: string,
    updatedItems: CartItem[],
): Promise<UpdateCartLineItemsResponse> => {
    const res = await fetchWithRetries(
        `${apiBaseUrl}/external/client/${client.clientHash}/cart/${cartUuid}/update`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                landingPageHash,
                updatedItems: mapLines(updatedItems, true),
            }),
        },
    )
    if (!res.ok) {
        return {
            isError: true,
            error: res.statusText,
        }
    }
    const resp = await res.json()
    return resp as UpdateCartLineItemsResponse
}

export const upsertCart = async (
    cart: Cart,
    client: ApiClient,
    cartUuid: string,
    landingPageHash: string,
    discountCodes?: string[],
    eventParams?: {
        shop?: string
        eventId?: string | null
        pEventId?: string | null
    },
): Promise<UpsertCartResponse> => {
    const [localShopifyY, cookieShopifyY] = localAndCookieKeys('_shopify_y')
    const shopifyY = cookieShopifyY || localShopifyY

    const endpoint = eventParams
        ? `${nextPrefix}/api/cart`
        : `${apiBaseUrl}/external/cart`

    let cartToken: string | undefined = undefined
    try {
        if (!(await hasValidCart(cartUuid, landingPageHash))) {
            // There isn't a valid cart for this cartUUID, so create one using the front-end
            // token and send it up for adoption in upsetCart.
            const cart = await createBlankCart(client)
            if (cart?.id) {
                // Successfully created a cart, let's pass it as the cartToken to adopt.
                cartToken = cart.id.replace('gid://shopify/Cart/', '')
            }
        }
    } catch (error) {
        console.warn('Tokenless cart creation failure in upsertCart', error)
    }

    const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            cartUuid,
            clientHash: client.clientHash,
            lines: mapLines(cart.items),
            landingPageHash,
            discountCodes,
            attributes: buildCartAttributes(shopifyY),
            // Encapsulate event params in event field
            ...(eventParams && { event: eventParams }),
            cartToken,
        }),
    })

    if (!res.ok) {
        return {
            isError: true,
            error: res.statusText,
        }
    }
    const resp = await res.json()
    return resp as UpsertCartResponse
}

export const getReviews = async (
    link: Pick<ApiFullLandingPageLink, 'linkHash' | 'shopifyId'>,
    page: number,
    shopifyProductId?: string | null,
): Promise<GetReviewsResponse> => {
    const params = new URLSearchParams({
        linkShopifyId: link.shopifyId,
        linkHash: link.linkHash,
        page: String(page),
    })
    if (shopifyProductId) {
        params.set('shopifyProductId', shopifyProductId)
    }
    const res = await fetch(`${nextPrefix}/api/link-reviews?${params}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    })
    const resp = await res.json()
    return resp
}

export const getReviewMedia = async (
    linkHash: string,
    page: number,
    shopifyProductId?: string | null,
): Promise<GetReviewMediaResponse> => {
    const params = new URLSearchParams({
        linkHash,
        page: String(page),
    })
    if (shopifyProductId) {
        params.set('shopifyProductId', shopifyProductId)
    }
    const res = await fetch(`${nextPrefix}/api/link-review-media?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    })
    const resp = await res.json()
    return resp
}

export const getPlaylist = async (
    playlistHash: string,
): Promise<ApiPlaylist> => {
    const res = await fetch(`${nextPrefix}/api/playlist/${playlistHash}`)
    const resp = await res.json()
    return resp.result.playlist as ApiPlaylist
}

export const getLandingPageLinks = async (
    linkHashes: string[],
    landingPageHash?: string,
): Promise<ApiFullLandingPageLink[]> => {
    // Filter out empty or invalid link hashes
    const validLinkHashes = linkHashes.filter(
        (hash) => hash && hash.trim().length > 0,
    )

    // If no valid hashes, return empty array
    if (validLinkHashes.length === 0) {
        return []
    }

    const body: { linkHashes: string[]; landingPageHash?: string } = {
        linkHashes: validLinkHashes,
    }
    const trimmedLp = landingPageHash?.trim()
    if (trimmedLp) {
        body.landingPageHash = trimmedLp
    }

    const res = await fetch(`${nextPrefix}/api/landing-page-links`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    })

    // Handle non-OK responses
    if (!res.ok) {
        console.error('Failed to fetch landing page links:', res.statusText)
        return []
    }

    return res.json()
}

/**
 * Client-side fetch for a single landing page link by Shopify product ID.
 * Routes through the Next.js cached proxy so CDN caching applies.
 * Returns null when no link exists for the product; throws on upstream errors.
 */
export const getLandingPageLinkForProduct = async (
    clientHash: string,
    productId: string,
): Promise<ApiFullLandingPageLink | null> => {
    const res = await fetchWithRetries(
        `${nextPrefix}/api/client/${clientHash}/landing-page-link/${productId}`,
    )
    if (!res.ok) {
        throw new Error(
            `Failed to fetch landing page link for product: '${productId}' with status "${res.statusText}"`,
        )
    }
    const data = await res.json()
    return data as ApiFullLandingPageLink | null
}

export const getCollectionProducts = async (
    landingPageHash: string,
    collectionId: string,
): Promise<ApiFullLandingPageLink[]> => {
    const res = await fetchWithRetries(
        `${nextPrefix}/api/landing/${landingPageHash}/collection-products/${encodeURIComponent(collectionId)}`,
    )
    if (!res.ok) {
        throw new Error(
            `Failed to fetch collection products for collectionId '${collectionId}': ${res.statusText}`,
        )
    }
    const data = await res.json()
    if (!data || data.isError) {
        throw new Error(
            `Failed to fetch collection products for collectionId '${collectionId}': ${
                data?.message ?? 'unknown error'
            }`,
        )
    }
    return data.result?.landingPageLinks ?? []
}

export const getLandingPageProducts = async (
    landingPageHash: string,
    search: string = typeof window === 'undefined'
        ? ''
        : window.location.search,
): Promise<GetExternalLandingPageProductsResponse | null> => {
    try {
        const query = isProductFamiliesQueryEnabled(search)
            ? `?${PRODUCT_FAMILIES_PARAM}=1`
            : ''
        const res = await fetchWithRetries(
            `${nextPrefix}/api/landing/${landingPageHash}/products${query}`,
        )
        if (!res.ok) {
            return null
        }
        const data = await res.json()
        if (!data || data.isError) {
            return null
        }
        return data.result ?? null
    } catch {
        return null
    }
}

export const getSellingBrief = async (
    landingPageHash: string,
    signal: AbortSignal,
): Promise<BrandChatStorefrontContext | null> => {
    const res = await fetch(
        `${nextPrefix}/api/landing/${landingPageHash}/selling-brief`,
        { signal },
    )
    if (!res.ok) return null
    const data = await res.json()
    return data?.isError ? null : (data?.result ?? null)
}

export const sendBrandChatTurn = async (
    landingPageHash: string,
    messages: BrandChatMessage[],
    onProgress: (message: string) => void,
): Promise<BrandChatReply> => {
    const res = await fetch(
        `${nextPrefix}/api/landing/${landingPageHash}/brand-chat/turn`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messages }),
        },
    )
    if (!res.ok || !res.body) {
        throw new Error('Brand chat reply failed')
    }
    for await (const event of readTurnEvents(res.body)) {
        if (event.type === 'progress') onProgress(event.message)
        if (event.type === 'reply') return event.reply
        if (event.type === 'error') break
    }
    throw new Error('Brand chat reply failed')
}
